import { useTranslation } from 'react-i18next';

import { Checkbox, Text } from '@pukka/ui-components';
import { useSnapshot } from 'valtio';

import { BrandsPersistedState, BrandsState } from '../Brands.state';
import { BrandsAssignModal, BrandsDeleteModal } from '../Modal';

import { BrandsFloatingMenu } from './BrandsFloatingMenu';
import { BrandsTableBody } from './BrandsTableBody';
import warning from './illustration-warning.png';

function BrandsTable({
  state = BrandsState,
  persistedState = BrandsPersistedState,
  withCheckbox,
}: {
  state?: typeof BrandsState;
  persistedState?: typeof BrandsPersistedState;
  withCheckbox: boolean;
}) {
  const { t } = useTranslation('library');

  const { parentList, outlets } = useSnapshot(persistedState) as typeof BrandsPersistedState;
  const {
    checkedAll,
    status,
    totalChecked,
    showBulkDeleteModal,
    showBulkMoveModal,
    showSelectAll,
    isLoadingBulkAction,
    isBulkDeleteAllOutlet,
  } = useSnapshot(state);

  const {
    handleCheckAll,
    handleUncheckAll,
    handleToggleBulkDeleteModal,
    handleToggleBulkDeleteAllOutletModal,
    handleBulkDelete,
    handleToggleBulkMoveModal,
    handleBulkMove,
  } = state;

  const hasManyOutlets = outlets.length > 1;

  const hasBrandButNoParent = parentList?.results.length === 1 && parentList?.results[0].id === -1;

  const showNoData = status.parentList === 'error' || (status.parentList === 'success' && parentList?.results.length === 0);

  return (
    <div className="relative h-[calc(100vh-170px)]">
      {/* Start of header */}
      <div className="flex items-center rounded-t-xl bg-neutral-200 py-4">
        <div className="w-[72px]" />
        {withCheckbox && showSelectAll && (
          <div className="px-6">
            <Checkbox size="md" checked={checkedAll} disabled={showNoData} onChange={handleCheckAll} />
          </div>
        )}
        <div className="flex-1 px-4">
          <Text className="text-neutral-700">{t('brands.name')}</Text>
        </div>
        <div className="px-4">
          <Text className="text-neutral-700">{t('brands.itemStock')}</Text>
        </div>
      </div>
      {/* End of header */}

      {status.parentList === 'error' || (status.parentList === 'success' && parentList?.results.length === 0) ? (
        <div className="m-auto flex max-w-4xl flex-col items-center justify-center p-10">
          <img src={warning} alt="No Data" className="mb-6" />
          <Text variant="title-1" weight="semibold" className="mb-4">
            {t('brands.noDataTitle')}
          </Text>
          <Text className="text-center">
            {t('brands.noDataDescription')}{' '}
            <Text as="a" href="https://help.mokapos.com/s/?language=in" target="_blank" className="underline" color="blue">
              {t('brands.noDataLink')}
            </Text>
          </Text>
        </div>
      ) : (
        <BrandsTableBody withCheckbox={withCheckbox} />
      )}

      {(totalChecked.brandCount > 0 || totalChecked.parentCount > 0) && (
        <BrandsFloatingMenu
          parentCount={totalChecked.parentCount}
          brandCount={totalChecked.brandCount}
          disableBulkMove={totalChecked.brandCount === 0 || hasBrandButNoParent}
          hideDeleteFromAllOutlets={!hasManyOutlets}
          onUncheckAll={handleUncheckAll}
          onClickDelete={handleToggleBulkDeleteModal}
          onClickDeleteAllOutlet={handleToggleBulkDeleteAllOutletModal}
          onClickMoveTo={handleToggleBulkMoveModal}
        />
      )}

      <BrandsDeleteModal
        open={showBulkDeleteModal && !isBulkDeleteAllOutlet}
        variant="bulk"
        isLoading={isLoadingBulkAction}
        onToggle={handleToggleBulkDeleteModal}
        onClickDelete={handleBulkDelete}
      />

      <BrandsDeleteModal
        open={showBulkDeleteModal && isBulkDeleteAllOutlet}
        variant="bulk_all_outlet"
        isLoading={isLoadingBulkAction}
        onToggle={handleToggleBulkDeleteAllOutletModal}
        onClickDelete={handleBulkDelete}
      />

      <BrandsAssignModal
        open={showBulkMoveModal}
        isButtonLoading={isLoadingBulkAction}
        heading="Assign Principal"
        description="Select a Principal for this brand or opt to have no principal"
        searchPlaceholder="Search principal"
        variant="parent"
        onToggleModal={handleToggleBulkMoveModal}
        onSubmit={handleBulkMove}
      />
    </div>
  );
}

export { BrandsTable };
